import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BellRing, CheckCircle2, XCircle, Tag, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const StatusPill = ({ status }) => {
  const map = { paid: "bg-[#E6DFD4] text-black", pending: "bg-[#222] text-[#A3A3A3]", initiated: "bg-[#222] text-[#A3A3A3]", failed: "bg-red-900/40 text-red-300" };
  return <span className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase font-bold ${map[status] || map.pending}`}>{status}</span>;
};

const Stat = ({ label, value }) => (
  <div className="border border-[#1c1c1c] bg-[#0A0A0A] p-6">
    <div className="text-[11px] tracking-[0.25em] uppercase text-[#737373] mb-3">{label}</div>
    <div className="font-display text-4xl text-[#E6DFD4]">{value}</div>
  </div>
);

const ChannelRow = ({ label, res }) => (
  <div data-testid={`notif-${label.toLowerCase()}`} className="flex items-start gap-3 border border-[#1c1c1c] bg-[#0A0A0A] p-4">
    {res.ok ? <CheckCircle2 size={18} className="text-[#E6DFD4] mt-0.5 shrink-0" /> : <XCircle size={18} className="text-red-400 mt-0.5 shrink-0" />}
    <div>
      <div className="font-display text-xl uppercase tracking-wide text-[#F5F5F5]">{label}</div>
      <div className="text-xs text-[#A3A3A3] mt-1">{res.ok ? "Sent successfully." : (res.error || "Not attempted.")}</div>
    </div>
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [notif, setNotif] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", type: "percent", value: "", min_subtotal: "" });
  const [sweeping, setSweeping] = useState(false);

  useEffect(() => {
    api.get("/admin/orders")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.status === 403 ? "Admin access required." : "Failed to load orders."));
    api.get("/admin/promos").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/promos", {
        code: promoForm.code, type: promoForm.type,
        value: parseFloat(promoForm.value) || 0,
        min_subtotal: parseFloat(promoForm.min_subtotal) || 0,
      });
      setPromos((prev) => [data, ...prev.filter((p) => p.code !== data.code)]);
      setPromoForm({ code: "", type: "percent", value: "", min_subtotal: "" });
      toast.success(`Code ${data.code} saved`);
    } catch {
      toast.error("Could not create code");
    }
  };

  const togglePromo = async (code) => {
    try {
      const { data } = await api.patch(`/admin/promos/${code}`);
      setPromos((prev) => prev.map((p) => (p.code === code ? { ...p, active: data.active } : p)));
    } catch {
      toast.error("Could not update code");
    }
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/admin/abandoned-sweep");
      toast.success(`Sweep done — ${data.nudged} reminder(s) sent, ${data.eligible} eligible`);
    } catch {
      toast.error("Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setNotif(null);
    try {
      const { data } = await api.post("/admin/test-notification");
      setNotif(data.result);
      const okAny = data.result.email.ok || data.result.whatsapp.ok;
      toast[okAny ? "success" : "error"](okAny ? "Test sent on working channels" : "No channel succeeded — see details");
    } catch (e) {
      toast.error("Could not run test");
    } finally {
      setTesting(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    setUpdating(orderId);
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status });
      setData((prev) => ({ ...prev, orders: prev.orders.map((o) => (o.order_id === orderId ? { ...o, fulfillment_status: status } : o)) }));
      const wa = data.notification?.whatsapp;
      const label = status.replace(/_/g, " ");
      if (wa?.ok) toast.success(`Marked "${label}" — customer notified on WhatsApp`);
      else toast.message(`Marked "${label}"`, { description: wa?.error || "Customer WhatsApp not sent" });
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdating(null);
    }
  };


  return (
    <div className="min-h-screen bg-[#050505] px-5 md:px-10 pt-28 pb-24">
      <div className="max-w-[1300px] mx-auto">
        <button onClick={() => navigate("/account")} className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#A3A3A3] hover:text-[#E6DFD4] mb-8">
          <ArrowLeft size={15} /> Account
        </button>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-5xl md:text-7xl uppercase leading-none text-[#F5F5F5] mb-10">
          Order Dashboard
        </motion.h1>

        {error && <p data-testid="admin-error" className="text-red-400">{error}</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8" data-testid="admin-stats">
              <Stat label="Total Orders" value={data.stats.total_orders} />
              <Stat label="Paid Orders" value={data.stats.paid_orders} />
              <Stat label="Revenue" value={`AED ${data.stats.revenue}`} />
            </div>

            <div className="border border-[#1c1c1c] bg-[#0A0A0A] p-6 mb-12">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div>
                  <h2 className="font-display text-2xl uppercase text-[#F5F5F5]">Order Alerts</h2>
                  <p className="text-xs text-[#A3A3A3] mt-1">Send a sample alert to check email + WhatsApp are reaching you.</p>
                </div>
                <button
                  data-testid="test-notification-button"
                  onClick={runTest}
                  disabled={testing}
                  className="flex items-center gap-2 bg-[#E6DFD4] text-black px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors duration-300 disabled:opacity-50"
                >
                  <BellRing size={15} /> {testing ? "Sending..." : "Send Test Alert"}
                </button>
              </div>
              {notif && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="notif-results">
                  <ChannelRow label="Email" res={notif.email} />
                  <ChannelRow label="WhatsApp" res={notif.whatsapp} />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              <div className="border border-[#1c1c1c] bg-[#0A0A0A] p-6" data-testid="promo-panel">
                <div className="flex items-center gap-2 mb-5">
                  <Tag size={18} className="text-[#E6DFD4]" />
                  <h2 className="font-display text-2xl uppercase text-[#F5F5F5]">Promo Codes</h2>
                </div>
                <form onSubmit={createPromo} className="grid grid-cols-2 gap-3 mb-5">
                  <input data-testid="promo-code-input" required placeholder="CODE" value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} className="col-span-2 bg-[#0D0D0D] border border-[#222] focus:border-[#E6DFD4] outline-none text-[#F5F5F5] px-3 py-2.5 text-sm uppercase tracking-[0.1em]" />
                  <select data-testid="promo-type-select" value={promoForm.type} onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value })} className="bg-[#0D0D0D] border border-[#222] text-[#E6DFD4] px-3 py-2.5 text-xs uppercase tracking-[0.1em] outline-none">
                    <option value="percent">% Percent</option>
                    <option value="fixed">AED Fixed</option>
                  </select>
                  <input data-testid="promo-value-input" required type="number" step="0.01" placeholder={promoForm.type === "percent" ? "e.g. 10" : "e.g. 50"} value={promoForm.value} onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })} className="bg-[#0D0D0D] border border-[#222] focus:border-[#E6DFD4] outline-none text-[#F5F5F5] px-3 py-2.5 text-sm" />
                  <input data-testid="promo-min-input" type="number" step="0.01" placeholder="Min subtotal (0)" value={promoForm.min_subtotal} onChange={(e) => setPromoForm({ ...promoForm, min_subtotal: e.target.value })} className="col-span-2 bg-[#0D0D0D] border border-[#222] focus:border-[#E6DFD4] outline-none text-[#F5F5F5] px-3 py-2.5 text-sm" />
                  <button data-testid="promo-create-button" type="submit" className="col-span-2 bg-[#E6DFD4] text-black py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors">Save Code</button>
                </form>
                <div className="space-y-2" data-testid="promo-list">
                  {promos.length === 0 && <p className="text-xs text-[#737373]">No codes yet.</p>}
                  {promos.map((p) => (
                    <div key={p.code} data-testid={`promo-row-${p.code}`} className="flex items-center justify-between border border-[#161616] px-3 py-2.5">
                      <div>
                        <span className="font-display text-lg tracking-wide text-[#F5F5F5]">{p.code}</span>
                        <span className="text-xs text-[#A3A3A3] ml-2">{p.type === "percent" ? `${p.value}% off` : `AED ${p.value} off`}{p.min_subtotal ? ` · min ${p.min_subtotal}` : ""} · used {p.used_count}</span>
                      </div>
                      <button data-testid={`promo-toggle-${p.code}`} onClick={() => togglePromo(p.code)} className={`px-3 py-1 text-[10px] tracking-[0.15em] uppercase font-bold ${p.active ? "bg-[#E6DFD4] text-black" : "border border-[#2c2c2c] text-[#737373]"}`}>
                        {p.active ? "Active" : "Off"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-[#1c1c1c] bg-[#0A0A0A] p-6" data-testid="abandoned-panel">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw size={18} className="text-[#E6DFD4]" />
                  <h2 className="font-display text-2xl uppercase text-[#F5F5F5]">Abandoned Carts</h2>
                </div>
                <p className="text-xs text-[#A3A3A3] mb-5">Customers who added items but didn't check out get an automatic WhatsApp reminder (runs in the background). You can also trigger a sweep now.</p>
                <button data-testid="abandoned-sweep-button" onClick={runSweep} disabled={sweeping} className="flex items-center gap-2 border border-[#2c2c2c] text-[#E6DFD4] px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#111] transition-colors disabled:opacity-50">
                  <RefreshCw size={15} className={sweeping ? "animate-spin" : ""} /> {sweeping ? "Sweeping..." : "Run Reminder Sweep"}
                </button>
              </div>
            </div>

            <div className="border border-[#1c1c1c] overflow-x-auto" data-testid="admin-orders-table">
              <table className="w-full text-left min-w-[820px]">
                <thead>
                  <tr className="text-[11px] tracking-[0.2em] uppercase text-[#737373] border-b border-[#1c1c1c]">
                    <th className="p-4 font-medium">Order</th>
                    <th className="p-4 font-medium">Customer</th>
                    <th className="p-4 font-medium">Items</th>
                    <th className="p-4 font-medium">Total</th>
                    <th className="p-4 font-medium">Payment</th>
                    <th className="p-4 font-medium">Fulfillment</th>
                    <th className="p-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="border-b border-[#141414] text-sm text-[#C9C4BB] align-top">
                      <td className="p-4 text-xs text-[#737373]">{o.order_id}</td>
                      <td className="p-4">
                        <div className="text-[#F5F5F5]">{o.name}</div>
                        <div className="text-xs text-[#737373]">{o.email}</div>
                        <div className="text-xs text-[#737373]">{o.phone}</div>
                        <div className="text-xs text-[#737373]">{o.address?.line}, {o.address?.emirate}</div>
                      </td>
                      <td className="p-4 text-xs">{o.items.map((i) => `${i.name} x${i.quantity}`).join(", ")}</td>
                      <td className="p-4 font-display text-xl text-[#E6DFD4]">AED {o.total}</td>
                      <td className="p-4"><StatusPill status={o.payment_status === "paid" ? "paid" : o.status} /></td>
                      <td className="p-4">
                        <select
                          data-testid={`fulfillment-select-${o.order_id}`}
                          value={o.fulfillment_status || "pending"}
                          disabled={updating === o.order_id || o.payment_status !== "paid"}
                          onChange={(e) => updateStatus(o.order_id, e.target.value)}
                          className="bg-[#0D0D0D] border border-[#2c2c2c] text-[#E6DFD4] text-xs uppercase tracking-[0.1em] px-3 py-2 outline-none focus:border-[#E6DFD4] disabled:opacity-40 cursor-pointer"
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="out_for_delivery">Out for Delivery</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="p-4 text-xs text-[#737373]">{new Date(o.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {data.orders.length === 0 && (
                    <tr><td colSpan={7} className="p-10 text-center text-[#A3A3A3]">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
